import { Badge, Tag, theme } from "antd";
import { EntityTypes } from "@vzbl/integrations";
import { attributeToTitle } from "src/utils/strings";
import { generateLink } from "../workstreams/common";
import { useEntityContext } from "../useEntityContext";
import { Hover } from "src/components/Hover";

const defaultColor = "#989CFF";

const colorWheel = {
  red: "#EC8786",
  orange: "#E98D61",
  amber: "#DB9640",
  yellow: "#C5A231",
  lime: "#B0AA39",
  green: "#97B14C",
  emerald: "#7DB664",
  teal: "#5AB983",
  cyan: "#39BAA4",
  sky: "#2AB6D1",
  blue: "#58ACF2",
  indigo: "#79A4FE",
  violet: "#989CFF",
  purple: "#B394F4",
  fuchsia: "#CA8DE1",
  pink: "#DC88C7",
  rose: "#E886A7",
};

export type TagSearchParams = {
  entityType: typeof EntityTypes;
  orgId: string;
  attribute: string;
  value: string;
};

const colors: { [key: string]: string } = {
  //status
  open: colorWheel.emerald,
  inProgress: colorWheel.blue,
  completed: colorWheel.teal,

  //state
};

const getColor = (text: string): string => {
  if (text === undefined || text === null || text === "") {
    return defaultColor;
  }
  if (colors[text]) {
    return colors[text];
  }
  //use a hash function to get a heuristic color from the color wheel
  const hash = [...text].reduce((acc, _char, index) => {
    return acc + (text.codePointAt(index) || 0);
  }, 0);
  const index = hash % Object.keys(colorWheel).length;

  return Object.values(colorWheel)[index] || defaultColor;
};

export const VzblTag = (props: {
  orgId: string;
  text: string;
  linkParams?: TagSearchParams;
  entityUuid?: string;
  color?: string;
  formatText?: boolean;
  context: ReturnType<typeof useEntityContext>;
  asLink?: boolean;
}) => {
  const { token } = theme.useToken();
  const { useHistory, setUuid } = props.context;
  if (props.text === undefined || props.text === null || props.text === "") {
    return <></>;
  }

  const openLink = () => {
    if (useHistory && props.entityUuid) {
      setUuid(props.entityUuid);
    } else if (props.entityUuid) {
      window.open(`/${props.orgId}/entity/${props.entityUuid}`, "_blank");
    } else if (props.linkParams) {
      window.open(
        generateLink(
          props.linkParams.entityType.toString(),
          props.linkParams.orgId,
          props.linkParams.attribute,
          props.linkParams.value
        ),
        "_blank"
      );
    }
  };
  const clickable = props.linkParams || props.entityUuid;
  const color = clickable ? "none" : props.color || getColor(props.text);

  if (props.asLink)
    return (
      <span className="tag-link" onClick={() => openLink()}>
        {props.formatText ? attributeToTitle(props.text) : props.text}
      </span>
    );
  return (
    <Hover
      hoverStyle={{
        border: `1px solid ${token.colorPrimary}`,
        backgroundColor: token.colorPrimaryBgHover,
        color: token.colorPrimaryTextHover,
      }}
    >
      <Tag
        color="default"
        className={`tag ${clickable ? "tag-clickable" : ""}`}
        onClick={() => openLink()}
      >
        {color != "none" && <Badge color={color} />}{" "}
        {props.formatText ? attributeToTitle(props.text) : props.text}
      </Tag>
    </Hover>
  );
};
